'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Toaster as SonnerToaster } from '../../components/ui/sonner';
import { AuthProvider, useAuth } from '@/components/auth/AuthContext';
import { safeStorage } from '../../lib/safe-storage';
import { apiClient } from '../../lib/api-client';
import { normalizeMediaUrl } from '@/utils/media';
import { APP_CONFIG } from '../../config';

type Theme = 'light' | 'dark';

export interface User {
  id: string;
  name: string;
  email: string;
  type: 'admin' | 'employer' | 'jobseeker' | 'super_admin';
  company?: string;
  profile?: {
    skills?: string[];
    experience?: string;
    resume?: string;
  };
}

export interface Job {
  id: string;
  title: string;
  company: string;
  companyId?: string;
  companyLogoUrl?: string | null;
  location: string;
  type: 'mega_job' | 'premium_job' | 'prime_job' | 'latest_job' | 'newspaper_job';
  category: string;
  salary: string;
  experience: string;
  description: string;
  requirements: string[];
  benefits: string[];
  postedDate: string;
  deadline: string;
  logo: string;
  // Optional cover image displayed on job cards
  coverImageUrl?: string | null;
  featured: boolean;
  tier: 'mega_job' | 'premium_job' | 'prime_job' | 'latest_job' | 'newspaper_job' | 'megajob' | 'premium' | 'prime' | 'latest' | 'newspaper';
  source?: 'online' | 'newspaper';
  publishedDate: string;
  tags: string[];
  desiredCandidate?: string;
  companyJobCount?: number;
  allCompanyJobs?: Job[];
  isApplied?: boolean;
}

export interface Application {
  id: string;
  jobId: string;
  userId: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  appliedAt: string;
  jobTitle?: string;
  company?: string;
  applicationData?: any;
}

interface AppContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  jobs: Job[];
  setJobs: (jobs: Job[]) => void;
  applications: Application[];
  setApplications: (applications: Application[]) => void;
  savedJobs: string[];
  setSavedJobs: (savedJobs: string[]) => void;
  users: User[];
  setUsers: (users: User[]) => void;
  companies: any[];
  setCompanies: (companies: any[]) => void;
  filters: any;
  setFilters: (filters: any) => void;
  handleApplyJob: (jobId: string, applicationData?: any) => void;
  handleSaveJob: (jobId: string) => { requiresLogin?: boolean; removed?: boolean; added?: boolean };
  // Add followed companies support
  followedCompanies: string[];
  setFollowedCompanies: (companyIds: string[]) => void;
  handleFollowCompany: (companyId: string) => { requiresLogin?: boolean; removed?: boolean; added?: boolean };
  siteSettings: SiteSettings;
  setSiteSettings: (settings: SiteSettings) => void;
  contentVersion?: number;
  getProfileCompletion: (user?: User | null) => number;
  syncResumes: () => Promise<void>;
  resumes: any[];
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

// Component to sync AuthContext user with AppProvider currentUser
function UserSyncWrapper({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { setCurrentUser } = useApp();

  useEffect(() => {
    const effectiveId = (user as any)?.id || (user as any)?._id;
    if (user && effectiveId) {
      // Convert AuthContext user to AppProvider user format
      const appUser: User = {
        id: effectiveId,
        name: user.full_name || user.email || 'User',
        email: user.email || '',
        type: user.user_type === 'job_seeker' ? 'jobseeker' : (user.user_type as 'admin' | 'employer' | 'jobseeker' | 'super_admin'),
        company: undefined,
        profile: {
          skills: user.profile?.skills || [],
          experience: user.profile?.experience,
          resume: user.profile?.resume_url
        }
      };
      setCurrentUser(appUser);
    } else {
      setCurrentUser(null);
    }
  }, [user, setCurrentUser]);

  return <>{children}</>;
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>('light');

  // Helper function to check if we're on an authentication route
  const isAuthRoute = () => {
    const path = window.location.pathname;
    return path.includes('/auth/') ||
      path.includes('/login') ||
      path.includes('/register') ||
      path.includes('/signup') ||
      path.includes('/forgot-password') ||
      path.includes('/reset-password') ||
      path === '/admin/login' ||
      path === '/super-admin/login';
  };

  // Limit realtime subscription on routes where it isn't needed
  const isRealtimeRoute = () => {
    const path = window.location.pathname;
    if (isAuthRoute()) return false;
    return true;
  };

  // State declarations
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [savedJobs, setSavedJobs] = useState<string[]>([]);
  // Followed companies state
  const [followedCompanies, setFollowedCompanies] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    location: '',
    type: '',
    category: '',
    salary: '',
    experience: ''
  });
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [resumes, setResumes] = useState<any[]>([]); // New state for resumes
  const [contentVersion, setContentVersion] = useState<number>(0);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({
    siteName: APP_CONFIG.siteName,
    siteUrl: APP_CONFIG.siteUrl,
    tagline: APP_CONFIG.tagline,
    description: APP_CONFIG.description,
    metaDescription: `${APP_CONFIG.siteName} - ${APP_CONFIG.tagline}. Find jobs, post jobs, connect with employers and job seekers.`,
    keywords: 'jobs nepal, employment, careers, hiring, job search, kathmandu jobs',
    email: APP_CONFIG.emails.contact,
    phone: APP_CONFIG.company.phone,
    address: APP_CONFIG.company.address,
    supportEmail: APP_CONFIG.emails.support,
    businessEmail: APP_CONFIG.emails.business,
    companyLocation: APP_CONFIG.company.location,
    mapUrl: APP_CONFIG.maps.embedUrl,
    googleMapsLink: APP_CONFIG.maps.link,
    coordinates: APP_CONFIG.company.coordinates,
    facebook: APP_CONFIG.socials.facebook,
    instagram: APP_CONFIG.socials.instagram,
    twitter: APP_CONFIG.socials.twitter,
    linkedin: APP_CONFIG.socials.linkedin,
    youtube: APP_CONFIG.socials.youtube,
    companyName: APP_CONFIG.company.name,
    registrationNumber: 'REG12345',
    taxNumber: 'TAX67890',
    establishedYear: String(new Date().getFullYear()),
    jobBadgeLabel: 'mega_job',
    mainPhone: APP_CONFIG.company.phone,
    supportPhone: APP_CONFIG.phones.support,
    mediaPressPhone: APP_CONFIG.phones.media,
    hrPhone: APP_CONFIG.phones.hr,
    mediaPressEmail: APP_CONFIG.emails.media,
    careersEmail: APP_CONFIG.emails.careers,
    addressLine1: 'Putalisadak, Kathmandu',
    addressLine2: 'Bagmati Province, Nepal',
    poBox: 'P.O. Box: 12345',
    weekdayHours: 'Monday - Friday: 9:00 AM - 6:00 PM',
    saturdayHours: 'Saturday: 10:00 AM - 4:00 PM',
    sundayHours: 'Sunday: Closed',
    holidayHours: 'Public Holidays: Closed'
  });

  // Load site settings from backend (Direct DB Connection)
  useEffect(() => {
    let cancelled = false;
    const loadSettings = async () => {
      try {
        const config = await apiClient.getSiteConfig();
        if (!cancelled && config && typeof config === 'object') {
          setSiteSettings(prev => ({
            ...(prev as any),
            ...(config as any),
            coordinates: {
              ...((prev as any).coordinates || { latitude: '', longitude: '' }),
              ...((config as any).coordinates || {})
            },
            // Legacy field mapping
            mediaPressEmail: (config as any).mediaPressEmail || (config as any).salesEmail || (prev as any).mediaPressEmail,
            mediaPressPhone: (config as any).mediaPressPhone || (config as any).salesPhone || (prev as any).mediaPressPhone,
          } as any));
        }
      } catch (err) {
        console.warn('Failed to load site config from API', err);
      }
    };
    loadSettings();
    return () => { cancelled = true; };
  }, []);


  useEffect(() => {
    try {
      const raw = (siteSettings as any)?.faviconUrl;
      const url = normalizeMediaUrl(raw) || '/Favicon.ico';
      const setIcon = (rel: string) => {
        let link = document.querySelector(`link[rel='${rel}']`) as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement('link');
          link.rel = rel;
          document.head.appendChild(link);
        }
        link.href = url;
        const lower = String(url).toLowerCase();
        if (lower.endsWith('.ico')) link.type = 'image/x-icon';
        else if (lower.endsWith('.png')) link.type = 'image/png';
        else if (lower.endsWith('.svg')) link.type = 'image/svg+xml';
        else link.removeAttribute('type');
      };
      setIcon('icon');
      setIcon('shortcut icon');
    } catch { }
  }, [siteSettings]);

  useEffect(() => {
    if (!Array.isArray(companies) || companies.length === 0) return;
    setJobs(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const byId: Record<string, any> = {};
      const byName: Record<string, any> = {};
      for (const c of companies) {
        const id = String((c as any)?._id || (c as any)?.id || '').trim();
        if (id) byId[id] = c;
        const name = String((c as any)?.name || (c as any)?.company_name || '').trim().toLowerCase();
        if (name) byName[name] = c;
      }
      return prev.map((j: any) => {
        const cid = String((j as any)?.companyId || '').trim();
        const cname = String((j as any)?.company || '').trim().toLowerCase();
        let comp = (cid && byId[cid]) || (cname && byName[cname]) || null;
        if (!comp) {
          try {
            const locStr = String((j as any)?.location || '').trim();
            if (locStr && locStr.includes(' - ')) {
              const [bNameRaw, cityRawAndAddr] = locStr.split(' - ');
              const bName = String(bNameRaw || '').trim().toLowerCase();
              const cityRaw = String(cityRawAndAddr || '').trim();
              const city = cityRaw.split(',')[0].trim().toLowerCase();
              for (const c of companies) {
                const branches = Array.isArray((c as any)?.branches) ? (c as any).branches : [];
                const match = branches.find((b: any) => {
                  const bn = String(b?.name || '').trim().toLowerCase();
                  const ct = String(b?.city || '').trim().toLowerCase();
                  return bn === bName && ct === city;
                });
                if (match) { comp = c; break; }
              }
            }
          } catch { }
        }
        if (!comp) return j as Job;
        const nextName = String((comp as any)?.name || (comp as any)?.company_name || (j as any)?.company || '').trim();
        const rawLogo = (comp as any)?.logo_url || (comp as any)?.logoUrl || (comp as any)?.logo || (comp as any)?.profileImage || null;
        const normalizedLogo = normalizeMediaUrl(rawLogo) || (rawLogo || null);
        const nextCompanyId = (j as any)?.companyId || String((comp as any)?._id || (comp as any)?.id || '');
        let nextLocation = (j as any)?.location || '';
        try {
          const locStr = String((j as any)?.location || '').trim();
          if (locStr && locStr.includes(' - ')) {
            const bname = locStr.split(' - ')[0].trim().toLowerCase().replace(/^branch\s+/i, '');
            const branches = Array.isArray((comp as any)?.branches) ? (comp as any).branches : [];
            const br = branches.find((b: any) => String(b?.name || '').trim().toLowerCase() === bname);
            if (br) {
              const addr = String(br?.address || '').trim();
              nextLocation = `${br.name} - ${br.city}${addr ? ', ' + addr : ''}`;
            }
          }
        } catch { }
        const changed = nextName !== (j as any)?.company || normalizedLogo !== (j as any)?.companyLogoUrl || nextCompanyId !== (j as any)?.companyId || nextLocation !== (j as any)?.location;
        if (!changed) return j as Job;
        return {
          ...(j as any),
          company: nextName,
          companyId: nextCompanyId,
          companyLogoUrl: normalizedLogo,
          location: nextLocation,
        } as Job;
      });
    });
  }, [companies]);

  const getProfileCompletion = (user?: User | null) => {
    const u = user ?? currentUser;
    if (!u) return 0;

    // Try to get detailed resume data from internal state first, then storage
    let resumeData: any = null;
    try {
      const list = resumes.length > 0 ? resumes : (() => {
        const listRaw = safeStorage.getItem('resumes');
        return listRaw ? JSON.parse(listRaw) : [];
      })();

      if (Array.isArray(list) && list.length > 0) {
        // Sort by last modified
        const sorted = [...list].sort((a: any, b: any) =>
          new Date(b.lastModified || b.updatedAt || b.updated_at || b.created_at || 0).getTime() -
          new Date(a.lastModified || a.updatedAt || a.updated_at || a.created_at || 0).getTime()
        );
        const latest = sorted[0];

        // Verify ownership to prevent stale data usage
        const resumeUserId = (latest as any).userId || (latest as any).jobSeekerId || (latest as any).job_seeker_id;
        const currentUserId = u?.id || (u as any)?._id;
        // STRICT: Both must exist and match. Legacy/orphan data is rejected for current user.
        const isOwner = !!resumeUserId && !!currentUserId && String(resumeUserId) === String(currentUserId);

        if (latest && isOwner) {
          // If the list item has detailed data, use it. 
          if (latest.personalInfo || latest.experience) {
            resumeData = latest;
          } else if (latest.id || latest._id) {
            const raw = safeStorage.getItem(`resume_${latest.id || latest._id}`);
            resumeData = raw ? JSON.parse(raw) : (resumes.length > 0 ? latest : null);
            // Verify ownership of detailed data too
            if (resumeData) {
              const rUserId = resumeData.userId || resumeData.jobSeekerId || (resumeData as any).job_seeker_id;
              if (rUserId && currentUserId && String(rUserId) !== String(currentUserId)) {
                resumeData = null;
              }
            }
          }
        }
      }
    } catch { }

    const personal = (resumeData && resumeData.personalInfo) || {};
    const summary = String((resumeData && resumeData.summary) || '').trim();
    const educ = Array.isArray(resumeData?.education) ? resumeData.education : [];
    const exp = Array.isArray(resumeData?.experience) ? resumeData.experience : [];
    const skillsResume = Array.isArray(resumeData?.skills) ? resumeData.skills : [];

    // 1. Name
    const name = String(personal.fullName || u.name || '').trim();

    // 2. Email
    const email = String(personal.email || u.email || '').trim();

    // 3. Phone
    const phone = String(personal.phone || '').trim();

    // 4. Skills (Strict: Must come from Builder)
    const skillsArr = (skillsResume && skillsResume.length > 0) ? skillsResume : [];
    const hasSkills = skillsArr.length > 0;

    // 5. Experience (Strict: Must come from Builder)
    // Check experience status (fresher, student, etc. skip strict experience check if selected)
    const expStatus = (resumeData && resumeData.experienceStatus) || 'experienced';
    const isExperienced = expStatus === 'experienced';
    // If not experienced (fresher/student), we consider experience "complete" (skipped)
    // If experienced, must have valid entry
    const hasExperience = !isExperienced || (exp.length > 0 && exp.some((e: any) => (e.jobTitle || e.role) && (e.company || e.employer)));

    // 6. Education (Strict: Must come from Builder)
    const hasEducation = educ.length > 0 && educ.some((e: any) => e.institution && e.degree);

    // 7. Summary (Strict: Builder) or Uploaded Resume (Legacy)
    // We allow an uploaded file to count as "Summary/Resume" section compliance
    const rawResume = String(u.profile?.resume || '').trim();
    const normalizedResume = normalizeMediaUrl(rawResume) || rawResume;
    const hasResumeFile = !!normalizedResume;
    const hasSummary = !!summary;
    const hasSummaryOrResume = hasSummary || hasResumeFile;

    // Weighted Calculation
    // Total = 100
    // Personal (Name, Email, Phone) = 15
    // Skills = 20
    // Experience = 25
    // Education = 25
    // Summary = 15

    let score = 0;
    if (name) score += 5;
    if (email) score += 5;
    if (phone) score += 5;
    if (hasSkills) score += 20;
    if (hasExperience) score += 25;
    if (hasEducation) score += 25;
    if (hasSummaryOrResume) score += 15;

    return score;
  };

  // Load site settings from storage
  useEffect(() => {
    try {
      const raw = safeStorage.getItem('site_settings');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setSiteSettings({
            ...siteSettings,
            ...parsed
          });
        }
      }
    } catch (e) {
      console.warn('Failed to load site settings from storage');
    }
  }, []);



  useEffect(() => {
    if (!isRealtimeRoute()) return;
    let es: EventSource | null = null;
    const onMessage = (ev: MessageEvent) => {
      try {
        const evt = JSON.parse(ev.data);
        if (evt?.topic === 'jobs.created' && evt?.payload) {
          const dbJob = evt.payload;
          const mapped: Job = {
            id: String(dbJob.id || dbJob._id || Math.random()),
            title: String(dbJob.title || ''),
            company: String((dbJob.company && dbJob.company.name) || dbJob.company || dbJob.company_id || ''),
            companyId: String(dbJob.company_id || dbJob.companyId || ''),
            location: (() => {
              const loc = String(dbJob.location || '').trim();
              if (loc) return loc;
              const branchId = String((dbJob as any).branch_id || (dbJob as any).branchId || '').trim();
              if (!branchId) return '';
              try {
                const companyBranches = (dbJob.company && (dbJob.company.branches || [])) || [];
                const br = Array.isArray(companyBranches) ? companyBranches.find((b: any) => String(b.id || b._id) === branchId) : undefined;
                const derived = br?.city || br?.name || '';
                return String(derived || '').trim();
              } catch { return ''; }
            })(),
            type: String(dbJob.type || dbJob.tier || ''),
            category: String((dbJob.category && dbJob.category.name) || dbJob.category_id || ''),
            salary: (() => {
              const currency = String(dbJob.salary_currency || 'NPR');
              const toNum = (v: any) => {
                const n = parseInt(String(v ?? '').replace(/[^0-9]/g, ''));
                return Number.isFinite(n) ? n : null;
              };
              const nmin = toNum(dbJob.salary_min);
              const nmax = toNum(dbJob.salary_max);
              if (nmin && nmax) return `${currency} ${nmin.toLocaleString()} - ${nmax.toLocaleString()}`;
              const s = String(dbJob.salary || dbJob.salary_range || '').trim();
              return s;
            })(),
            experience: String(dbJob.experience_level || dbJob.experience || ''),
            description: String(dbJob.description || ''),
            requirements: Array.isArray(dbJob.requirements)
              ? dbJob.requirements
              : (typeof dbJob.requirements === 'string' ? [dbJob.requirements] : []),
            benefits: Array.isArray(dbJob.benefits) ? dbJob.benefits : [],
            postedDate: dbJob.created_at ? new Date(dbJob.created_at).toISOString() : new Date().toISOString(),
            deadline: dbJob.deadline ? new Date(dbJob.deadline).toISOString() : '',
            logo: String((dbJob.company && dbJob.company.logo_url) || dbJob.logo || ''),
            companyLogoUrl: (() => {
              const cid = String(dbJob.company_id || dbJob.companyId || '').trim();
              if (cid) {
                const base = `/uploads/company-logos/${cid}`;
                const norm = normalizeMediaUrl(base);
                return norm || base;
              }
              const fromCompany = (dbJob.company && (dbJob.company.logo_url || dbJob.company.logo)) || '';
              const normCompany = normalizeMediaUrl(String(fromCompany || ''));
              return normCompany || (fromCompany || null);
            })(),
            coverImageUrl: (dbJob.cover_image_url || dbJob.coverImageUrl || dbJob.coverImage || null),
            featured: !!dbJob.featured,
            tier: String(dbJob.tier || ''),
            source: String(dbJob.source || ''),
            publishedDate: dbJob.published_at ? new Date(dbJob.published_at).toISOString() : (dbJob.created_at ? new Date(dbJob.created_at).toISOString() : new Date().toISOString()),
            tags: Array.isArray(dbJob.tags) ? dbJob.tags : []
          } as any;

          setJobs(prev => {
            const exists = prev.some(j => j.id === mapped.id);
            if (exists) return prev;
            return [mapped, ...prev];
          });
        } else if (evt?.topic === 'jobs.updated' && evt?.payload) {
          const p = evt.payload;
          const id = String(p.id || p._id || '');
          if (!id) return;
          setJobs(prev => prev.map(j => {
            if (String(j.id) !== id) return j;
            const merged = { ...j } as any;
            if (p.title) merged.title = p.title;
            if (p.location) merged.location = p.location;
            if (p.tier) merged.tier = p.tier as any;
            if (p.featured !== undefined) merged.featured = !!p.featured;
            if (p.status) merged.status = p.status as any;
            if (p.company) merged.company = p.company as any;
            if (p.description) merged.description = p.description as any;
            if (Array.isArray(p.requirements) || typeof p.requirements === 'string') {
              merged.requirements = Array.isArray(p.requirements) ? p.requirements : [p.requirements];
            }
            if (p.deadline) merged.deadline = String(p.deadline);
            if (Array.isArray(p.tags) || typeof p.tags === 'string') {
              merged.tags = Array.isArray(p.tags) ? p.tags : String(p.tags).split(',').map(t => t.trim()).filter(Boolean);
            }
            if (Array.isArray(p.skills) || typeof p.skills === 'string') {
              merged.benefits = merged.benefits || [];
              merged.skills = Array.isArray(p.skills) ? p.skills : String(p.skills).split(',').map(s => s.trim()).filter(Boolean);
            }
            if (p.cover_image_url) merged.coverImageUrl = p.cover_image_url as any;
            if (p.desired_candidate || p.desiredCandidate) {
              merged.desiredCandidate = (p.desired_candidate || p.desiredCandidate) as any;
            }
            return merged as Job;
          }));
        } else if (evt?.topic === 'jobs.deleted' && evt?.payload) {
          const p = evt.payload;
          const id = String(p.id || p._id || '');
          if (!id) return;
          setJobs(prev => prev.filter(j => String(j.id) !== id));
        } else if (evt?.topic && evt.topic.startsWith('companies.')) {
          const p = evt.payload || {};
          if (evt.topic === 'companies.created') {
            setCompanies(prev => {
              const id = String(p.id || p._id || '');
              if (id && prev.some(c => String(c._id || c.id) === id)) return prev;
              const next = [{ _id: id || p._id, name: p.name || 'Company', location: p.location || '', ...p }, ...prev];
              return next;
            });
          } else if (evt.topic === 'companies.updated') {
            setCompanies(prev => prev.map(c => {
              const id = String(c._id || c.id || '');
              const pid = String(p.id || p._id || p.employer_id || '');
              if (id && pid && id === pid) {
                return { ...c, ...p };
              }
              return c;
            }));
          } else if (evt.topic === 'companies.deleted') {
            setCompanies(prev => prev.filter(c => String(c._id || c.id) !== String(p.id)));
          }
          setContentVersion(v => v + 1);
        } else if (evt?.topic === 'applications.created' || evt?.topic === 'applications.updated') {
          if (currentUser?.id) {
            (async () => {
              try {
                const res = await apiClient.getApplications();
                const items = Array.isArray(res?.applications) ? res.applications : [];
                const mapped: Application[] = items.map((a: any) => ({
                  id: a._id || a.id || String(Date.now()),
                  jobId: a.job_id || a.jobId,
                  userId: a.job_seeker_id || a.userId,
                  status: a.status || 'pending',
                  appliedAt: a.applied_at ? new Date(a.applied_at).toISOString() : new Date().toISOString(),
                  jobTitle: a.job?.title,
                  company: a.job?.company,
                }));
                setApplications(mapped);
              } catch { }
            })();
          }
        } else if (evt?.topic === 'blogs.updated' || evt?.topic === 'news.updated' || evt?.topic === 'siteInfo.about.updated' || evt?.topic === 'siteInfo.team.updated' || (evt?.topic && evt.topic.startsWith('recruitment.'))) {
          setContentVersion(v => v + 1);
        } else if (evt?.topic === 'site_settings.site_info.updated' || evt?.topic === 'site_info.updated') {
          const config = evt.topic === 'site_info.updated' ? evt.payload : evt.payload?.config;
          if (config && typeof config === 'object') {
            setSiteSettings(prev => {
              // Check if actual values changed before updating and toast
              const hasChanged = Object.keys(config).some(key =>
                key !== 'coordinates' && JSON.stringify((prev as any)[key]) !== JSON.stringify((config as any)[key])
              );

              if (!hasChanged) return prev;

              const next = {
                ...(prev as any),
                ...(config as any),
                coordinates: {
                  ...((prev as any)?.coordinates || { latitude: '', longitude: '' }),
                  ...(((config as any)?.coordinates) || {})
                },
                // Legacy field mapping
                mediaPressEmail: (config as any).mediaPressEmail || (config as any).salesEmail || (prev as any).mediaPressEmail,
                mediaPressPhone: (config as any).mediaPressPhone || (config as any).salesPhone || (prev as any).mediaPressPhone,
              } as any;

              // Update reflected instantly without redundant toast notification
              return next;
            });
          }
        }
      } catch (_) { }
    };

    let controller: { stop: () => void } | null = null;
    (async () => {
      const ok = await apiClient.isBackendAvailable();
      if (!ok) return;
      controller = apiClient.subscribeRealtimePersistent([
        'jobs.created',
        'jobs.updated',
        'jobs.deleted',
        'companies.created',
        'companies.updated',
        'companies.deleted',
        'blogs.updated',
        'news.updated',
        'siteInfo.about.updated',
        'siteInfo.team.updated',
        'applications.created',
        'applications.updated',
        'recruitment.created',
        'recruitment.updated',
        'recruitment.deleted',
        'site_settings.site_info.updated'
      ], onMessage as any);
    })();

    return () => { try { controller && controller.stop(); } catch (_) { } };
  }, []);

  // Sync resumes from backend
  const syncResumes = async () => {
    if (!currentUser?.id || currentUser.type !== 'jobseeker') return;
    try {
      const res = await apiClient.request('/resumes');
      const list = Array.isArray((res as any)?.resumes)
        ? (res as any).resumes
        : (Array.isArray((res as any)?.data) ? (res as any).data : (Array.isArray(res) ? res : []));

      if (Array.isArray(list)) {
        setResumes(list);
        safeStorage.setItem('resumes', JSON.stringify(list));
        // Also try to sync individual resumes to storage for fallback usage
        list.forEach((r: any) => {
          if (r.id || r._id) safeStorage.setItem(`resume_${r.id || r._id}`, JSON.stringify(r));
        });
      }
    } catch (err) {
      console.warn('Resume sync failed', err);
    }
  };

  useEffect(() => {
    syncResumes();
  }, [currentUser?.id, currentUser?.type]);

  // Persist site settings to storage
  useEffect(() => {
    try {
      safeStorage.setItem('site_settings', JSON.stringify(siteSettings));
    } catch (e) {
      console.warn('Failed to persist site settings');
    }
  }, [siteSettings]);

  // Load tier configuration from backend section 'job_tiers'
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getSectionSettings('job_tiers');
        const cfg = res?.config || {};
        if (!cancelled && cfg && typeof cfg === 'object') {
          setSiteSettings(prev => ({ ...prev, tierConfig: cfg }));
        }
      } catch (err) {
        console.warn('Failed to load job_tiers config', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Load saved jobs from backend
    const loadSavedJobs = async () => {
      if (currentUser?.id) {
        try {
          // Try backend first
          const saved = await apiClient.getSavedJobs();
          if (Array.isArray(saved)) {
            setSavedJobs(saved);
          }
        } catch (e) {
          console.warn('Failed to load saved jobs from backend');
        }
      } else {
        setSavedJobs([]);
      }
    };
    loadSavedJobs();
  }, [currentUser?.id]);

  // Persist saved jobs via API (handled in handler)
  // Removed localStorage effect

  // Load followed companies from backend
  useEffect(() => {
    const loadFollowed = async () => {
      if (currentUser?.id) {
        try {
          const followed = await apiClient.getFollowedCompanies();
          if (Array.isArray(followed)) {
            setFollowedCompanies(followed);
          }
        } catch (e) {
          console.warn('Failed to load followed companies from backend');
        }
      } else {
        setFollowedCompanies([]);
      }
    };
    loadFollowed();
  }, [currentUser?.id]);

  // Persist followed companies via API (handled in handler)
  // Removed localStorage effect

  // Load companies from backend
  useEffect(() => {
    // Skip data fetching on authentication routes
    if (isAuthRoute()) {
      return;
    }

    let isMounted = true;

    const loadCompaniesFromBackend = async () => {
      try {
        const res: any = await apiClient.getCompanies({ limit: 100 });
        const companiesArr = Array.isArray(res?.companies)
          ? res.companies
          : Array.isArray(res)
            ? res
            : [];
        if (isMounted && Array.isArray(companiesArr)) {
          const normalized = companiesArr.map((c: any) => {
            const logoUrl = c?.logo_url || c?.logoUrl || c?.logo || c?.company_logo || c?.profileImage || null;
            const normalizedLogo = normalizeMediaUrl(logoUrl) || (logoUrl || '');
            const coverUrl = c?.cover_image_url || c?.coverImageUrl || c?.banner_url || c?.bannerUrl || c?.coverImage || null;
            const normalizedCover = normalizeMediaUrl(coverUrl) || (coverUrl || '');
            return {
              ...c,
              id: c?._id || c?.id,
              logo_url: normalizedLogo,
              logo: c?.logo ? (normalizeMediaUrl(c?.logo) || c?.logo) : normalizedLogo,
              logoUrl: c?.logoUrl ? (normalizeMediaUrl(c?.logoUrl) || c?.logoUrl) : normalizedLogo,
              cover_image_url: normalizedCover,
              coverImageUrl: normalizedCover,
              banner_url: c?.banner_url ? (normalizeMediaUrl(c?.banner_url) || c?.banner_url) : normalizedCover,
            };
          });
          setCompanies(normalized);
        }
      } catch (err) {
        console.warn('Failed to fetch companies from backend:', err);
      }
    };

    const fallbackEmployers = async () => {
      try {
        const res: any = await (apiClient as any).getEmployers?.({ limit: 100 });
        const employersArr = Array.isArray(res?.companies)
          ? res.companies
          : Array.isArray(res)
            ? res
            : [];
        if (isMounted && Array.isArray(employersArr) && employersArr.length && (!companies || companies.length === 0)) {
          const normalized = employersArr.map((c: any) => {
            const logoUrl = c?.logo_url || c?.logoUrl || c?.logo || c?.company_logo || c?.profileImage || null;
            const normalizedLogo = normalizeMediaUrl(logoUrl) || (logoUrl || '');
            const coverUrl = c?.cover_image_url || c?.coverImageUrl || c?.banner_url || c?.bannerUrl || c?.coverImage || null;
            const normalizedCover = normalizeMediaUrl(coverUrl) || (coverUrl || '');
            return {
              ...c,
              id: c?._id || c?.id,
              logo_url: normalizedLogo,
              logo: c?.logo ? (normalizeMediaUrl(c?.logo) || c?.logo) : normalizedLogo,
              logoUrl: c?.logoUrl ? (normalizeMediaUrl(c?.logoUrl) || c?.logoUrl) : normalizedLogo,
              cover_image_url: normalizedCover,
              coverImageUrl: normalizedCover,
              banner_url: c?.banner_url ? (normalizeMediaUrl(c?.banner_url) || c?.banner_url) : normalizedCover,
            };
          });
          setCompanies(normalized);
        }
      } catch (_) {
        // no-op
      }
    };

    loadCompaniesFromBackend().then(fallbackEmployers);

    return () => { isMounted = false; };
  }, []);

  // NEW: Load jobs from backend (no mock fallback)
  useEffect(() => {
    // Skip data fetching on authentication routes
    if (isAuthRoute()) {
      return;
    }

    let isMounted = true;

    const loadJobsFromBackend = async () => {
      try {
        const res: any = await apiClient.getJobs({ limit: 100, status: 'active' });
        const items = Array.isArray(res?.jobs) ? res.jobs : Array.isArray(res) ? res : [];
        // Build company industry lookup for better category mapping
        const companyMapById: Record<string, string> = {};
        try {
          (companies || []).forEach((c: any) => {
            const id = String(c?._id || c?.id || '');
            if (id) companyMapById[id] = c?.industry || c?.category || '';
          });
        } catch { }

        const mapped: Job[] = items.map((dbJob: any) => ({
          id: dbJob.id || dbJob._id || String(Math.random()),
          title: String(dbJob.title || ''),
          company: String((dbJob.company && dbJob.company.name) || dbJob.company || dbJob.company_id || ''),
          companyId: String(dbJob.company_id || dbJob.companyId || ''),
          companyLogoUrl: (() => {
            const cid = String(dbJob.company_id || dbJob.companyId || '').trim();
            if (cid) {
              const base = `/uploads/company-logos/${cid}`;
              const norm = normalizeMediaUrl(base);
              return norm || base;
            }
            const fromCompany = (dbJob.company && (dbJob.company.logo_url || dbJob.company.logo)) || '';
            const normCompany = normalizeMediaUrl(String(fromCompany || ''));
            return normCompany || (fromCompany || null);
          })(),
          location: (() => {
            const loc = String(dbJob.location || '').trim();
            if (loc) return loc;
            const branchId = String((dbJob as any).branch_id || (dbJob as any).branchId || '').trim();
            if (!branchId) return '';
            // Try derive from embedded company branches first
            try {
              const companyBranches = (dbJob.company && (dbJob.company.branches || [])) || [];
              const br = Array.isArray(companyBranches) ? companyBranches.find((b: any) => String(b.id || b._id) === branchId) : undefined;
              const derived = br?.city || br?.name || '';
              if (derived) return String(derived).trim();
            } catch { }
            // Fallback: derive from global companies by company_id
            try {
              const compId = String(dbJob.company_id || dbJob.companyId || '').trim();
              const comp = (companies || []).find((c: any) => String(c._id || c.id) === compId);
              const br = comp && Array.isArray((comp as any).branches) ? (comp as any).branches.find((b: any) => String(b.id || b._id) === branchId) : undefined;
              const derived = br?.city || br?.name || '';
              return String(derived || '').trim();
            } catch { }
            return '';
          })(),
          type: String(dbJob.type || dbJob.tier || ''),
          category: String((companyMapById[String(dbJob.company_id || dbJob.companyId || '')] || (dbJob.category && dbJob.category.name) || dbJob.category_id || '')),
          salary: (() => {
            const currency = String(dbJob.salary_currency || 'NPR');
            const type = String(dbJob.salary_type || dbJob.salaryType || '').trim().toLowerCase();
            const toNum = (v: any) => {
              const n = parseInt(String(v ?? '').replace(/[^0-9]/g, ''));
              return Number.isFinite(n) ? n : null;
            };
            // If negotiable, prefer formatted string even if legacy min/max exist
            if (type === 'negotiable' || type.includes('negotiable')) {
              const s = String(dbJob.salary || dbJob.salary_range || 'Negotiable').trim();
              return s || 'Negotiable';
            }
            const nmin = toNum(dbJob.salary_min);
            const nmax = toNum(dbJob.salary_max);
            if (nmin && nmax) return `${currency} ${nmin.toLocaleString()} - ${nmax.toLocaleString()}`;
            const s = String(dbJob.salary || dbJob.salary_range || '').trim();
            return s || 'Negotiable';
          })(),
          experience: String(dbJob.experience_level || dbJob.experience || ''),
          description: String(dbJob.description || ''),
          desiredCandidate: String(dbJob.desired_candidate || dbJob.desiredCandidate || ''),
          requirements: Array.isArray(dbJob.requirements)
            ? dbJob.requirements
            : (typeof dbJob.requirements === 'string' ? [dbJob.requirements] : []),
          benefits: Array.isArray(dbJob.benefits) ? dbJob.benefits : [],
          postedDate: dbJob.created_at ? new Date(dbJob.created_at).toISOString() : new Date().toISOString(),
          deadline: dbJob.deadline ? new Date(dbJob.deadline).toISOString() : '',
          logo: String((dbJob.company && dbJob.company.logo_url) || dbJob.logo || ''),
          coverImageUrl:
            dbJob.cover_image_url ||
            dbJob.coverImageUrl ||
            dbJob.coverImage ||
            dbJob.company?.banner_url ||
            null,
          featured: !!dbJob.featured,
          tier: String(dbJob.tier || dbJob.type || ''),
          source: String(dbJob.source || ''),
          publishedDate: dbJob.published_at ? new Date(dbJob.published_at).toISOString() : (dbJob.created_at ? new Date(dbJob.created_at).toISOString() : new Date().toISOString()),
          tags: Array.isArray(dbJob.tags) ? dbJob.tags : []
        }));
        if (isMounted) setJobs(mapped);
      } catch (err) {
        console.warn('Failed to fetch jobs from backend:', err);
        if (isMounted) setJobs([]);
      }
    };

    loadJobsFromBackend();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    // Skip data fetching on authentication routes
    if (isAuthRoute()) {
      return;
    }

    // Fetch applications for the current user from backend
    const fetchApplications = async () => {
      if (!currentUser?.id) {
        setApplications([]);
        return;
      }
      try {
        const res = await apiClient.getApplications();
        const items = Array.isArray(res?.applications) ? res.applications : [];
        const mapped: Application[] = items.map((a: any) => ({
          id: String(a._id || a.id || Date.now()),
          jobId: String(a.job_id || a.jobId || ''),
          userId: String(a.job_seeker_id || a.userId || ''),
          status: (a.status as Application['status']) || 'pending',
          appliedAt: a.applied_at ? new Date(a.applied_at).toISOString() : new Date().toISOString(),
          jobTitle: a.job?.title,
          company: a.job?.company,
        }));
        setApplications(mapped);
      } catch (error) {
        console.warn('Failed to load applications', error);
      }
    };
    fetchApplications();
  }, [currentUser?.id]);

  const handleApplyJob = async (jobId: string, applicationData?: any) => {
    try {
      console.log('📝 Applying for job:', jobId);

      if (!currentUser) {
        console.warn('No user logged in for job application');
        throw new Error('Please log in to apply for jobs');
      }

      if (!jobId) {
        console.error('No job ID provided for application');
        throw new Error('Invalid job ID');
      }

      const completionPercent = getProfileCompletion(currentUser);

      // Get latest resume ID for redirection if needed
      const sorted = [...resumes].sort((a: any, b: any) => {
        const ad = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime();
        const bd = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime();
        return bd - ad;
      });
      const latestResumeId = sorted[0]?._id || sorted[0]?.id || '';

      if (completionPercent < 80) {
        toast.error(`Your profile is ${completionPercent}% complete. Please reach at least 80% to apply. Update your resume in the Resume section.`);
        if (latestResumeId) {
          setTimeout(() => {
            try { window.location.href = `/resume-builder/${latestResumeId}`; } catch { }
          }, 1000);
        } else {
          try { window.location.href = '/jobseeker-dashboard/resume'; } catch { }
        }
        const err: any = new Error(`Your profile is ${completionPercent}% complete. Please reach at least 80% to apply.`);
        err.code = 'PROFILE_INCOMPLETE';
        err.status = 400;
        throw err;
      }

      // Check if already applied (client-side check first for UX)
      const alreadyApplied = applications.some(a => String(a.jobId) === String(jobId) && String(a.userId) === String(currentUser.id));
      if (alreadyApplied) {
        throw new Error('You have already applied for this job');
      }

      // Check if job exists
      const jobExists = jobs.some(j => String(j.id) === String(jobId));
      if (!jobExists) {
        console.error('Job not found:', jobId);
        throw new Error('Job not found or has been removed');
      }

      // Call backend API to submit application
      const apiApplicationData = {
        jobId,
        coverLetter: applicationData?.coverLetter || '',
        resumeUrl: applicationData?.resumeUrl || currentUser.profile?.resume || ''
      };

      const result = await apiClient.applyForJob(apiApplicationData);
      console.log('✅ Backend API response:', result);

      // Only update local state if backend accepted the application
      if (result && result.application) {
        // Find job details for application context
        const job = jobs.find(j => j.id === jobId);

        // Update local state with the new application
        const newApplication: Application = {
          id: result.application?.id || String(result.application?._id || Date.now()),
          jobId,
          userId: currentUser.id,
          status: 'pending',
          appliedAt: new Date().toISOString(),
          jobTitle: job?.title,
          company: job?.company,
          ...(applicationData && { applicationData })
        };

        setApplications([...applications, newApplication]);
        console.log('✅ Job application submitted successfully:', newApplication);
      }

      return result;
    } catch (error: any) {
      console.error('❌ Error applying for job:', error);
      // Parse backend error message if available
      const errorMessage = error?.data?.error || error?.message || 'Failed to submit application';
      const customError = new Error(errorMessage);
      (customError as any).status = error?.status || error?.code;
      (customError as any).code = error?.code;
      throw customError;
    }
  };

  const handleSaveJob = (jobId: string) => {
    if (!currentUser) {
      // This will be handled in the JobCard component now
      return { requiresLogin: true };
    }

    const isSaving = !savedJobs.includes(jobId);

    // Optimistic update
    if (!isSaving) {
      setSavedJobs(prev => prev.filter(id => id !== jobId));
    } else {
      setSavedJobs(prev => [...prev, jobId]);
    }

    // Sync with backend
    apiClient.toggleSavedJob(jobId).catch(err => {
      console.error('Failed to sync saved job:', err);
      // Revert if failed
      if (!isSaving) {
        setSavedJobs(prev => [...prev, jobId]);
      } else {
        setSavedJobs(prev => prev.filter(id => id !== jobId));
      }
      toast.error('Failed to save job preference');
    });

    return isSaving ? { added: true } : { removed: true };
  };

  const handleFollowCompany = (companyId: string) => {
    if (!currentUser) {
      return { requiresLogin: true };
    }
    const isFollowing = !followedCompanies.includes(companyId);

    // Optimistic update
    if (!isFollowing) {
      setFollowedCompanies(prev => prev.filter(id => id !== companyId));
    } else {
      setFollowedCompanies(prev => [...prev, companyId]);
    }

    // Sync with backend
    apiClient.toggleFollowedCompany(companyId).catch(err => {
      console.error('Failed to sync followed company:', err);
      // Revert if failed
      if (!isFollowing) {
        setFollowedCompanies(prev => [...prev, companyId]);
      } else {
        setFollowedCompanies(prev => prev.filter(id => id !== companyId));
      }
      toast.error('Failed to update company follow status');
    });

    return isFollowing ? { added: true } : { removed: true };
  };

  return (
    <AppContext.Provider
      value={{
        theme,
        setTheme,
        currentUser,
        setCurrentUser,
        jobs,
        setJobs,
        applications,
        setApplications,
        savedJobs,
        setSavedJobs,
        // expose followed companies
        followedCompanies,
        setFollowedCompanies,
        users,
        setUsers,
        companies,
        setCompanies,
        filters,
        setFilters,
        handleApplyJob,
        handleSaveJob,
        handleFollowCompany,
        siteSettings,
        setSiteSettings,
        contentVersion,
        getProfileCompletion,
        syncResumes,
        resumes,
      }}
    >
      {/* Remove nested AuthProvider to avoid conflicting auth contexts */}
      <UserSyncWrapper>
        {children}
      </UserSyncWrapper>
      <SonnerToaster position="top-right" expand={false} visibleToasts={3} duration={2500} closeButton />
    </AppContext.Provider>
  );
}


export interface SiteSettings {
  siteName: string;
  siteUrl: string;
  tagline: string;
  description: string;
  metaDescription: string;
  keywords: string;
  email: string;
  phone: string;
  address: string;
  supportEmail?: string;
  businessEmail?: string;
  companyLocation: string;
  mapUrl: string;
  googleMapsLink: string;
  coordinates: { latitude: string; longitude: string };
  facebook: string;
  instagram: string;
  twitter: string;
  linkedin: string;
  youtube: string;
  companyName: string;
  registrationNumber: string;
  taxNumber: string;
  establishedYear: string;
  // Optional department contacts
  mainPhone?: string;
  supportPhone?: string;
  mediaPressPhone?: string;
  hrPhone?: string;
  mediaPressEmail?: string;
  careersEmail?: string;
  contactRecipientEmail?: string;
  recaptchaSiteKey?: string;
  recaptchaVersion?: string;
  // Optional address details
  addressLine1?: string;
  addressLine2?: string;
  poBox?: string;
  // Public business hours
  weekdayHours?: string;
  saturdayHours?: string;
  sundayHours?: string;
  holidayHours?: string;
  // Branding assets
  logoUrl?: string;
  faviconUrl?: string;
  defaultImageUrl?: string;
  // New: override label shown on job cards
  jobBadgeLabel?: string;
  // New: job tier configuration loaded from backend section 'job_tiers'
  tierConfig?: Record<string, { label?: string; badgeClass?: string; textClass?: string; bgClass?: string }>;
}


